import {
  MICROSOFT365_READ_FEATURES,
  MICROSOFT365_READ_TOOLS,
  MICROSOFT365_TOOL_FEATURE,
  MICROSOFT365_WRITE_TOOLS,
  type Microsoft365Feature,
  type Microsoft365ReadTool,
  type Microsoft365SignInError,
  type Microsoft365State,
  type Microsoft365StatusDto,
  type Microsoft365TurnCode,
  type Microsoft365WriteTool,
} from '@dbrain/shared';

/**
 * What the person's Microsoft 365 screens show for a state (MsBaglantilar, MsBaglandi, MsDurumlar,
 * MsBagliHesaplar): DOM-free, so the words and buttons of every state are decided (and tested) in one place.
 * Texts are i18n keys under `microsoft365.user`.
 */

/** the pill's colour, the Bağlantılar cards' own tones (`.connector-state--*`) */
export type CardTone = 'ready' | 'account' | 'down' | 'off';

export type CardKind = 'hidden' | 'setup' | 'off' | 'connect' | 'reconnect' | 'consent' | 'unavailable' | 'connected';

export type CardPill = Exclude<Microsoft365State, 'unsupported'> | 'unavailable';

export interface CardView {
  kind: CardKind;
  /** `card.state.<pill>` */
  pill: CardPill;
  tone: CardTone;
  /** it cannot be used now: quiet colours that still keep 4.5:1, never dimmed with opacity */
  muted: boolean;
  /** the card's main button */
  action: 'connect' | 'reconnect' | 'consent' | 'retry' | null;
  /** the "…" menu: "Yeniden bağlan" when it can be used, "Bağlantıyı kaldır" whenever an account is kept */
  menu: { reconnect: boolean; remove: boolean };
  /** the person's own switch (connected only) */
  toggle: boolean;
  /** "Ayrıntılar" with the read tools */
  details: boolean;
}

const HIDDEN: CardView = {
  kind: 'hidden',
  pill: 'not_configured',
  tone: 'off',
  muted: true,
  action: null,
  menu: { reconnect: false, remove: false },
  toggle: false,
  details: false,
};

const OFF_STATES: readonly Microsoft365State[] = ['off_firm', 'not_allowed', 'off_team'];

/**
 * The Bağlantılar card. Hidden in a personal space and, for members, while the firm has not set it up; an admin
 * sees "Kurulmadı" with the way to Yönetim. `unavailable`: a sign-in just now found the server unable to reach
 * Microsoft (no lasting state says so; the next attempt decides again).
 */
export function cardView(status: Microsoft365StatusDto | undefined, unavailable = false): CardView {
  if (!status || status.state === 'unsupported') return HIDDEN;
  const kept = status.account !== null;
  if (status.state === 'not_configured') {
    return status.canManage ? { ...HIDDEN, kind: 'setup', pill: 'not_configured', menu: { reconnect: false, remove: kept } } : HIDDEN;
  }
  if (OFF_STATES.includes(status.state)) {
    return { ...HIDDEN, kind: 'off', pill: status.state as CardPill, menu: { reconnect: false, remove: kept } };
  }
  if (status.state === 'connected') {
    return { kind: 'connected', pill: 'connected', tone: 'ready', muted: false, action: null, menu: { reconnect: true, remove: true }, toggle: true, details: true };
  }
  if (unavailable) {
    return { kind: 'unavailable', pill: 'unavailable', tone: 'down', muted: false, action: 'retry', menu: { reconnect: false, remove: kept }, toggle: false, details: true };
  }
  if (status.state === 'needs_reconnect') {
    return { kind: 'reconnect', pill: 'needs_reconnect', tone: 'account', muted: false, action: 'reconnect', menu: { reconnect: false, remove: kept }, toggle: false, details: true };
  }
  if (status.state === 'consent_required') {
    return { kind: 'consent', pill: 'consent_required', tone: 'account', muted: false, action: 'consent', menu: { reconnect: true, remove: kept }, toggle: false, details: true };
  }
  return { kind: 'connect', pill: 'not_connected', tone: 'account', muted: false, action: 'connect', menu: { reconnect: false, remove: kept }, toggle: false, details: true };
}

/** Ayarlar › Hesap › Bağlı hesaplar lists Microsoft 365 when there is an account to see or remove, or one to connect. */
export function linkedVisible(status: Microsoft365StatusDto | undefined): boolean {
  return Boolean(status && status.state !== 'unsupported' && (status.account !== null || status.available));
}

/** `card.off.<key>`: why it cannot be used now, in the person's words */
export function offReason(status: Microsoft365StatusDto): 'offFirm' | 'notAllowed' | 'offTeam' | 'offTeamNamed' | 'notConfigured' {
  switch (status.state) {
    case 'off_firm':
      return 'offFirm';
    case 'not_allowed':
      return 'notAllowed';
    case 'off_team':
      return status.teamName ? 'offTeamNamed' : 'offTeam';
    default:
      return 'notConfigured';
  }
}

export interface ToolView {
  tool: Microsoft365ReadTool;
  feature: Microsoft365Feature;
  /** the firm has this feature on ("Neler açık") */
  on: boolean;
}

/** The six read tools in their order, each with whether the firm has its feature on (the write tools: writeToolsOf). */
export function toolsOf(features: Record<Microsoft365Feature, boolean>): ToolView[] {
  return MICROSOFT365_READ_TOOLS.map((tool) => ({ tool, feature: MICROSOFT365_TOOL_FEATURE[tool], on: features[MICROSOFT365_TOOL_FEATURE[tool]] }));
}

export interface WriteToolView {
  tool: Microsoft365WriteTool;
  feature: Microsoft365Feature;
  /** the account has granted its permission; false: "İzin gerekiyor" (the first use asks for it) */
  granted: boolean;
}

/** Part 3: the write tools of the switches the firm has on ("Ayrıntılar"), each always asking on a card. */
export function writeToolsOf(status: Pick<Microsoft365StatusDto, 'features' | 'grants'>): WriteToolView[] {
  return MICROSOFT365_WRITE_TOOLS.filter((tool) => status.features[MICROSOFT365_TOOL_FEATURE[tool]]).map((tool) => {
    const feature = MICROSOFT365_TOOL_FEATURE[tool];
    return { tool, feature, granted: status.grants?.[feature] === true };
  });
}

/** For "Şu an N bağlantı açık · asistan M araç kullanabilir": connected and switched on, with the tools the firm has on. */
export function summaryShare(status: Microsoft365StatusDto | undefined): { on: number; tools: number } {
  if (!status || status.state !== 'connected' || !status.enabled) return { on: 0, tools: 0 };
  return { on: 1, tools: toolsOf(status.features).filter((tool) => tool.on).length + writeToolsOf(status).length };
}

// ---- part 3: permissions asked for later ("Yeni izin gerekiyor · yeniden bağlan") -------------------------

const WRITE_FEATURES = ['mail_send', 'calendar_write'] as const satisfies readonly Microsoft365Feature[];
const DOCUMENT_FEATURES = ['onedrive_import', 'sharepoint_import'] as const satisfies readonly Microsoft365Feature[];

/** The firm has the feature on and the connected account has not granted its permission yet. */
export function needsGrant(status: Microsoft365StatusDto | undefined, feature: Microsoft365Feature): boolean {
  return Boolean(status?.account && status.features[feature] && status.grants?.[feature] !== true);
}

/**
 * What "Yeniden bağlan" asks Microsoft for in an upgrade sign-in: the features asked for (a card knows its own) that
 * the firm has on; without any, the write features that still need their permission (the chat's notice after
 * `microsoft_scopes_required`), else the document ones. Empty: a plain reconnect.
 */
export function upgradeFeatures(status: Microsoft365StatusDto | undefined, wanted?: readonly Microsoft365Feature[]): Microsoft365Feature[] {
  if (wanted && wanted.length > 0) return wanted.filter((feature) => !status || status.features[feature]);
  const writes = WRITE_FEATURES.filter((feature) => needsGrant(status, feature));
  if (writes.length > 0) return writes;
  return DOCUMENT_FEATURES.filter((feature) => needsGrant(status, feature));
}

/** `dialog.canItems.<key>`: the new promises of an upgrade sign-in, one per feature it asks for. */
export function upgradePromises(features: readonly Microsoft365Feature[]): ('send' | 'meetings' | 'documents' | 'onedrive' | 'sharepoint')[] {
  const has = (feature: Microsoft365Feature) => features.includes(feature);
  const documents = has('onedrive_import') && has('sharepoint_import') ? (['documents'] as const) : has('onedrive_import') ? (['onedrive'] as const) : has('sharepoint_import') ? (['sharepoint'] as const) : [];
  return [...(has('mail_send') ? (['send'] as const) : []), ...(has('calendar_write') ? (['meetings'] as const) : []), ...documents];
}

export type PromiseKey = 'find' | 'calendar' | 'attachments' | 'schedule' | 'documents' | 'onedrive' | 'sharepoint' | 'sendAndMeet' | 'send' | 'meetings';

/**
 * "Oppy şunları yapabilecek" / "Oppy şunları yapmaz" (MsBaglanOnay) for what the firm has on: part 2's reading
 * promises, then the documents and "Onayınızla mail göndermek ve toplantı oluşturmak" (part 3, restored when the
 * switches are on); the first thing Oppy does not do becomes "Onayınız olmadan mail göndermez, toplantı kurmaz".
 */
export function connectPromises(features: Record<Microsoft365Feature, boolean>): { can: PromiseKey[]; send: 'send' | 'sendApproval' | 'sendApprovalMail' | 'sendApprovalMeeting' } {
  const can: PromiseKey[] = [
    ...(features.mail_read ? (['find'] as const) : []),
    ...(features.calendar_read ? (['calendar'] as const) : []),
    ...(features.mail_read ? (['attachments'] as const) : []),
    ...(features.calendar_read ? (['schedule'] as const) : []),
    ...upgradePromises(DOCUMENT_FEATURES.filter((feature) => features[feature])),
    ...(features.mail_send && features.calendar_write ? (['sendAndMeet'] as const) : features.mail_send ? (['send'] as const) : features.calendar_write ? (['meetings'] as const) : []),
  ];
  const send = features.mail_send && features.calendar_write ? 'sendApproval' : features.mail_send ? 'sendApprovalMail' : features.calendar_write ? 'sendApprovalMeeting' : 'send';
  return { can, send };
}

export type LinkedKey = 'mail' | 'mailSend' | 'calendar' | 'calendarWrite' | 'onedrive' | 'sharepoint';

export interface LinkedRow {
  feature: Microsoft365Feature;
  key: LinkedKey;
  /** `linked.values.<value>`: "Okuma açık", "Her seferinde onayınızla", "Açık", "İzin gerekiyor", "Kapalı" */
  value: 'read' | 'approval' | 'on' | 'grant' | 'off';
}

const LINKED: { feature: Microsoft365Feature; key: LinkedKey; kind: 'read' | 'write' | 'documents' }[] = [
  { feature: 'mail_read', key: 'mail', kind: 'read' },
  { feature: 'mail_send', key: 'mailSend', kind: 'write' },
  { feature: 'calendar_read', key: 'calendar', kind: 'read' },
  { feature: 'calendar_write', key: 'calendarWrite', kind: 'write' },
  { feature: 'onedrive_import', key: 'onedrive', kind: 'documents' },
  { feature: 'sharepoint_import', key: 'sharepoint', kind: 'documents' },
];

/**
 * Ayarlar › Hesap › Bağlı hesaplar (MsBagliHesaplar): the reading rows always, part 3's rows while the firm has them
 * on: sending and meetings "Her seferinde onayınızla", OneDrive and SharePoint "Açık", or "İzin gerekiyor" while the
 * account has not granted the permission yet (no mockup for that state). The person's own switch off: "Kapalı".
 */
export function linkedRows(status: Microsoft365StatusDto): LinkedRow[] {
  return LINKED.filter((row) => row.kind === 'read' || status.features[row.feature]).map(({ feature, key, kind }) => {
    const on = status.features[feature] && status.enabled;
    if (!on) return { feature, key, value: 'off' };
    if (kind === 'read') return { feature, key, value: 'read' };
    if (status.grants?.[feature] !== true) return { feature, key, value: 'grant' };
    return { feature, key, value: kind === 'write' ? 'approval' : 'on' };
  });
}

/** Bağlantılar' card words while part 3's switches are on: what it is for, and its foot (never "yalnız okur" then). */
export function cardWords(features: Record<Microsoft365Feature, boolean>): { description: 'description' | 'descriptionWrite' | 'descriptionDocs' | 'descriptionAll'; writes: boolean } {
  const writes = features.mail_send || features.calendar_write;
  const documents = features.onedrive_import || features.sharepoint_import;
  return { description: writes && documents ? 'descriptionAll' : writes ? 'descriptionWrite' : documents ? 'descriptionDocs' : 'description', writes };
}

/** A URL from the server the browser may open: Microsoft's sign-in or code page (plain http only for the test fakes). */
export function openable(url: string): boolean {
  try {
    const protocol = new URL(url).protocol;
    return protocol === 'https:' || protocol === 'http:';
  } catch {
    return false;
  }
}

/** The reasons a sign-in can fail with (the redirect's `reason`, a device code's `failed`); anything else: `unknown`. */
const REASONS = [
  'state_invalid',
  'access_denied',
  'consent_required',
  'personal_account',
  'other_tenant',
  'guest_account',
  // D-194: the Microsoft account's address is not the person's Oppy e-mail address
  'wrong_account',
  'id_token_invalid',
  'exchange_failed',
  'not_available',
  'unavailable',
] as const satisfies readonly Microsoft365SignInError[];
export type ReasonKey = (typeof REASONS)[number] | 'unknown';

/** `reasons.<key>`: plain words for a failed sign-in, never the raw code */
export function reasonKey(reason: string | null | undefined): ReasonKey {
  return (REASONS as readonly string[]).includes(reason ?? '') ? (reason as ReasonKey) : 'unknown';
}

export type NoticeTone = 'info' | 'warning' | 'neutral' | 'success';
export type NoticeIcon = 'mail' | 'regenerate' | 'shield' | 'info' | 'lock' | 'done';

export interface NoticeView {
  tone: NoticeTone;
  icon: NoticeIcon;
  /** `notice.<key>.title` and `notice.<key>.text` */
  key: string;
  values?: Record<string, string>;
  /** connect / reconnect open the connect dialog; consent tells the admins; continue answers the question now; retry asks it again */
  action: 'connect' | 'reconnect' | 'consent' | 'continue' | 'retry' | 'connectors' | null;
}

const READY: NoticeView = { tone: 'success', icon: 'done', key: 'connected', action: 'continue' };

/** Why Microsoft 365 was off for the turn, as precisely as the person's state says now. */
function offNotice(status: Microsoft365StatusDto | undefined, latest: boolean): NoticeView {
  const quiet = { tone: 'neutral' as const, icon: 'lock' as const, action: null };
  if (!status) return { ...quiet, key: 'notAvailable' };
  switch (status.state) {
    case 'off_team':
      return status.teamName ? { ...quiet, key: 'offTeamNamed', values: { team: status.teamName } } : { ...quiet, key: 'offTeam' };
    case 'off_firm':
      return { ...quiet, key: 'offFirm' };
    case 'not_allowed':
      return { ...quiet, key: 'notAllowed' };
    case 'not_configured':
    case 'unsupported':
      return { ...quiet, key: 'notConfigured' };
    case 'connected': {
      if (!status.enabled) return { ...quiet, key: 'switchedOff', action: latest ? 'connectors' : null };
      // the read switches (part 3's switches start off and say nothing about a read turn)
      const off = MICROSOFT365_READ_FEATURES.filter((feature) => !status.features[feature]);
      if (off.length === 1) return { ...quiet, key: off[0] === 'mail_read' ? 'mailOff' : 'calendarOff' };
      if (off.length > 1) return { ...quiet, key: 'notAvailable' };
      return latest ? READY : { ...quiet, key: 'notAvailable' };
    }
    default:
      return { ...quiet, key: 'notAvailable' };
  }
}

/**
 * The chat's card after a turn stopped for Microsoft 365 (MsDurumlar). The words say what stopped the turn; only
 * the conversation's latest notice has a button, and when the person is connected by now (another tab, the
 * Bağlantılar page) that button answers the question instead of asking to connect again.
 */
export function noticeView(code: Microsoft365TurnCode, status: Microsoft365StatusDto | undefined, latest: boolean): NoticeView {
  const ready = Boolean(status && status.state === 'connected' && status.enabled);
  // switched off since the turn: say why, not "connect"
  const offNow = latest && status !== undefined && !status.available && code !== 'microsoft_unavailable';
  switch (code) {
    case 'microsoft_not_connected':
      if (latest && ready) return READY;
      if (offNow) return offNotice(status, latest);
      return { tone: 'info', icon: 'mail', key: 'notConnected', action: latest ? 'connect' : null };
    case 'microsoft_reconnect':
      if (latest && ready) return READY;
      if (offNow) return offNotice(status, latest);
      return { tone: 'warning', icon: 'regenerate', key: 'reconnect', action: latest ? 'reconnect' : null };
    case 'microsoft_consent_required':
      if (latest && ready) return READY;
      if (offNow) return offNotice(status, latest);
      return { tone: 'warning', icon: 'shield', key: 'consent', action: latest ? 'consent' : null };
    case 'microsoft_unavailable':
      // v0.15e.1: Microsoft could not be reached at all; the latest card offers to ask again (no mockup)
      return { tone: 'neutral', icon: 'info', key: status?.canManage ? 'unavailableAdmin' : 'unavailable', action: latest ? 'retry' : null };
    case 'microsoft_scopes_required':
      // part 3 (no mockup): the feature is on, the account has not granted its permission; "Yeniden bağlan" asks for it
      if (offNow) return offNotice(status, latest);
      // granted meanwhile (another tab): the question can go on
      if (latest && ready && status && upgradeFeatures(status).length === 0 && [...WRITE_FEATURES, ...DOCUMENT_FEATURES].some((feature) => status.features[feature])) return READY;
      return { tone: 'warning', icon: 'shield', key: 'scopes', action: latest ? 'reconnect' : null };
    default:
      return offNotice(status, latest);
  }
}
